import { Divider, Typography } from 'antd'
import { useRsfValues } from 'features/indicationTriage/cards/ffsCalc/calculations/calculateRsf'
import {
  DownwardIndicator,
  FfsResultIcon,
} from 'features/indicationTriage/cards/ffsCalc/FfsResultIcon'
import { useSavedReports } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsLtaPublishApi'
import { FfsReportPanel } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/FfsReportPanel'
import {
  FfsAnalysisResultsSection,
  useAnalysisResult,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/FfsResultPanel'
import { FfsPart } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import { useFfsScenarios } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/useFfsScenarios'
import { FfsSharedValuesContext } from 'features/indicationTriage/cards/ffsCalc/FfsSharedValues'
import { FfsAnalysisResult } from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/ffsResultScenarios'
import {
  FfsMawpSection,
  useFfsMawpProps,
} from 'features/indicationTriage/cards/ffsCalc/mawpSection/FfsMawpSection'
import { OptionalSection } from 'features/indicationTriage/cards/ffsCalc/optionalSection/OptionalSection'
import { useDebouncedLoading } from 'features/indicationTriage/cards/ffsCalc/useDebouncedLoading'
import { FfsCalcCardContentProps } from 'features/indicationTriage/cards/FfsCalcCard'
import React, { FC, useContext, useState } from 'react'

import { useFinalAcceptabilityCurveData } from './FfsAcceptabilityCriteriaCurve'
import { FfsDataValueSection } from './FfsDataValuesSection'
import {
  FfsFinalAcceptabilityCriteraSection,
  useLevelOneFinalAcceptabilityCriteriaValues,
} from './FfsFinalAcceptabilityCriteraSection'
import {
  FfsFlawSizeCriteriaSection,
  useLevelOneFlawSizeCriteriaCheck,
} from './FfsFlawSizeCriteriaSection'
import { FfsUserInputSection } from './FfsLevelOneUserInputSection'

const { Title } = Typography

const SPIN_DEBOUNCE_DELAY = 300

const RawFfsLevelOneCalc: FC<FfsCalcCardContentProps> = () => {
  const {
    fca,
    fcaMl,
    indicationItem,
    ffsDataValues,
    setFca,
    setFcaMl,
    jointEfficiency,
    setJointEfficiency,
    yieldStrength,
    setYieldStrength,
    selectedMawpCalcThickness,
    setSelectedMawpCalcThickness,
    calcType,
    setCalcType,
    designPressure,
  } = useContext(FfsSharedValuesContext)

  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(
    null
  )

  const {
    loading: dataValuesLoading = false,
    tc = null,
    tmm = null,
    innerDiameter = null,
    outerDiameter = null,
    lambda = null,
    nominal = null,
  } = ffsDataValues || {}

  const flawSizeCheckResult = useLevelOneFlawSizeCriteriaCheck({
    fca,
    tmm,
    fcaMl,
    tc,
    d: innerDiameter,
    indicationId: indicationItem?.id,
  })
  const {
    criteriaValues: flawSizeCriteriaCheckValues,
    isLoading: flawSizeCriteriaLoading,
  } = flawSizeCheckResult

  const loading = useDebouncedLoading(SPIN_DEBOUNCE_DELAY, [
    flawSizeCriteriaLoading,
    dataValuesLoading,
  ])

  const finalAcceptabilityCriteraValues =
    useLevelOneFinalAcceptabilityCriteriaValues({
      lambda,
      flawSizeCriteriaCheckValues,
    })
  const { foliasFactor, rsfa, setRsfa, rtThreshold } =
    finalAcceptabilityCriteraValues

  const { rsf, rt } = useRsfValues({ tmm, fcaMl, tc, fca, mt: foliasFactor })
  const finalAcceptabilityCurveData = useFinalAcceptabilityCurveData({
    lambda: lambda?.num ?? null,
    mt: foliasFactor?.num,
    rsf: rsf?.num ?? null,
    rsfa: rsfa?.num,
    rt: rt?.num ?? null,
    rtThreshold: rtThreshold?.num ?? null,
  })

  const mawpProps = useFfsMawpProps({
    t_c: tc,
    t_l: null,
    outerDiameter,
    rsfa,
    rsf,
    designPressure,
    tNom: nominal,
    ffsLevel: '1',
  })

  const {
    reports,
    isLoading: reportsLoading,
    forceReload: reloadReports,
    setDefaultReport,
  } = useSavedReports()

  const analysisResult: FfsAnalysisResult = useAnalysisResult({
    mawpr: mawpProps.mawpr,
    mawpDesign: mawpProps.mawpDesign,
    resultProps: {
      rsf,
      flawSizeCheckResult,
    },
  })

  // Hook to manage scenario selection and value synchronization
  useFfsScenarios({
    selectedScenarioId,
    setSelectedScenarioId,
    rsf_a: rsfa,
    setRsf_a: setRsfa, // rsfa is now properly settable
    e: mawpProps.qualityFactor,
    setE: mawpProps.setQualityFactor || (() => {}),
    y: mawpProps.pressureCoefficient,
    setY: mawpProps.setPressureCoefficient,
    w: jointEfficiency,
    setW: setJointEfficiency,
    s: yieldStrength,
    setS: setYieldStrength,
    selectedMawpCalcThickness: selectedMawpCalcThickness,
    setSelectedMawpCalcThickness: setSelectedMawpCalcThickness,
    tsl: mawpProps.tsl,
    setTsl: mawpProps.setTsl || (() => {}),
    fca,
    setFca,
    fca_ml: fcaMl,
    setFca_ml: setFcaMl,
    scenarios: reports,
    calcType,
    setCalcType,
    ffsLevel: '1',
    ffsPart: FfsPart.PART_5,
  })

  return (
    <>
      <Title level={4}>User Parameters</Title>
      <FfsUserInputSection />
      <Divider />
      <Title level={4}>Data Values</Title>
      {ffsDataValues && <FfsDataValueSection {...ffsDataValues} />}
      <Divider />
      <Title level={4}>Flaw Size Criteria</Title>
      <FfsFlawSizeCriteriaSection
        flawSizeCheckResult={flawSizeCheckResult}
        loading={loading}
      />
      <Divider />
      <Title level={4}>Final Acceptability Criteria</Title>
      <FfsFinalAcceptabilityCriteraSection
        {...finalAcceptabilityCriteraValues}
        curveDataProps={finalAcceptabilityCurveData}
        lambda={lambda}
        loading={loading}
      />
      <Divider>
        <FfsResultIcon
          passed={finalAcceptabilityCurveData.passesFac}
          tooltipFailureText='Failed acceptability criteria. Derating required (below)'
          trailingFail={DownwardIndicator}
        />
      </Divider>
      <OptionalSection visible={!finalAcceptabilityCurveData.passesFac}>
        <Title level={4}>MAWP</Title>
        <FfsMawpSection
          {...mawpProps}
          loading={loading}
          ffsLevel='1'
          addLongitudinalStress={false}
          showMawpCriteria={true}
        />
      </OptionalSection>
      <Divider />
      <Title level={4}>Results</Title>
      <FfsAnalysisResultsSection analysisResult={analysisResult} />
      <Divider />
      <Title level={4}>Reports</Title>
      <FfsReportPanel
        setSelectedScenarioId={(id) => setSelectedScenarioId(id ?? null)}
        flawSizeCriteriaCheck={flawSizeCheckResult}
        finalAcceptabilityCriteriaCheck={finalAcceptabilityCriteraValues}
        mawpProps={mawpProps}
        ffsPart={FfsPart.PART_5}
        ffsLevel='1'
        reports={reports}
        reportsLoading={reportsLoading}
        reloadReports={reloadReports}
        setDefaultReport={setDefaultReport}
        selectedScenarioId={selectedScenarioId}
        analysisResult={analysisResult}
      />
    </>
  )
}

export const FfsPartFiveLevelOneCalc = React.memo(RawFfsLevelOneCalc)
